import styled, { css } from 'styled-components'
import { ProductBranding } from '@velasca/ui/product-branding'
import { QuickAddToCartButton } from '@velasca/ui/quick-add-to-cart-button'
import { ResponsiveImage } from '@velasca/ui/responsive-image'
import { ReviewBadge } from '@velasca/ui/review-badge'

export const ApparelCard = styled.div<{ $isQuickAdd: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  max-width: 35rem;
  width: 100%;

  &.dynamic-size {
    width: clamp(23rem, 30vh, 35rem);
  }

  @media screen and (min-width: 768px) {
    &:hover:before {
      content: '';
      position: absolute;
      background-color: ${({ theme, $isQuickAdd }) =>
        $isQuickAdd ? theme.colors.background : 'inherit'};
      box-shadow: ${({ $isQuickAdd }) =>
        $isQuickAdd ? '0 0.4rem 2.2rem rgba(0, 0, 0, 0.03)' : 'none'};
    }
  }
`

export const Link = styled.a`
  color: unset !important;
  z-index: 1; // To make sure that QuickAdd doesn't overlap with price

  &:hover,
  &:link,
  &:visited,
  &:active {
    text-decoration: none;
  }
`

export const LinkFlex = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
`

// TODO: Here we didn't implemented the `SerifTitle` component beacause we have custom sizes, we should chang design at least for the `dynamic-size` to satisfy the `SerifTitle` component.
export const Title = styled.h3`
  text-align: center;
  display: block;
  width: 100%;
  font-family: ${({ theme }) => theme.fonts.serif.fontFamily};
  letter-spacing: -0.03rem;
  color: ${({ theme }) => theme.colors.primary};

  > span span {
    font-size: 3rem;
    line-height: 4.8rem;
  }

  font-size: 3rem;
  line-height: 4.8rem;

  @media screen and (min-width: 768px) {
    font-size: 3rem;
    line-height: 6.4rem;

    > span span {
      font-size: 3rem;
      line-height: 4.8rem;
    }
  }

  ${ApparelCard}.xs & {
    font-size: 2.1rem;
    line-height: 3.4rem;

    @media screen and (min-width: 768px) {
      font-size: 2.7rem;
      line-height: 4.2rem;
    }
  }

  ${ApparelCard}.lg & {
    font-size: 2.1rem;
    line-height: 3.4rem;

    @media screen and (min-width: 768px) {
      font-size: 3rem;
      line-height: 4.8rem;
    }
  }

  ${ApparelCard}.dynamic-size & {
    font-size: clamp(2.1rem, 3vh, 3.4rem);
  }
`

export const StyledProductBranding = styled(ProductBranding)`
  ${ApparelCard}.xs & {
    font-size: 1rem;
    padding: 0 0.4rem;
  }

  ${ApparelCard}.lg & {
    font-size: 1.1rem;
    padding: 0 1rem;

    @media screen and (min-width: 768px) {
      padding: 0 2.5rem;
    }
  }

  ${ApparelCard}.dynamic-size & {
    display: none;

    @media screen and (min-height: 1000px) {
      display: block;
      font-size: clamp(1rem, 1.5vh, 1.4rem);
    }
  }
`

export const Img = styled(ResponsiveImage)`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  object-fit: cover;
  width: 100%;
`

export const HoverImg = styled(Img)`
  display: none;

  @media (hover: hover) and (pointer: fine) {
    display: block;
  }
`

export const AbsoluteQuickAddToCartButton = styled(QuickAddToCartButton)`
  z-index: 1;
  position: absolute;
  top: -3px;
  left: -3px;
`

export const StaffPickImage = styled(ResponsiveImage)<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'block' : 'none')};
  position: absolute;
  z-index: 1;
  width: 9rem;
  top: 0;
  right: 0;

  ${ApparelCard}.xs & {
    width: 5rem;
  }

  ${ApparelCard}.lg & {
    width: 7rem;
  }

  @media screen and (min-width: 768px) {
    ${ApparelCard}.xs & {
      width: 6.5rem;
    }

    ${ApparelCard}.lg & {
      width: 11rem;
    }

    ${ApparelCard}.dynamic-size & {
      width: clamp(6.5rem, 9vh, 11rem);
    }
  }
`

interface ImgWrapperProps {
  visible: boolean
}

const ImgWrapper = styled.div<ImgWrapperProps>`
  display: ${({ visible }) => (visible ? 'block' : 'none')};
`

export const HoverImgWrapper = styled(ImgWrapper)`
  opacity: 0;
`
export const FrontImgWrapper = styled(ImgWrapper)`
  opacity: 1;
`

export const UpperSection = styled.div`
  overflow: hidden;
  position: relative;
  width: 100%;

  > span {
    display: block;
    line-height: 0;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${HoverImgWrapper} {
        opacity: 1;
      }

      ${FrontImgWrapper}:not(.single) {
        opacity: 0;
      }
    }
  }
`

export const Images = styled.div`
  position: relative;
  aspect-ratio: 4/5;

  .safari-lower-than-15 & {
    padding-top: 125%;
  }
`

export const ProductReviewBadge = styled(ReviewBadge)<{ current: boolean }>`
  display: ${({ current }) => (current ? 'flex' : 'none')};
  margin-bottom: 1rem;
  align-items: baseline;
`

export const ProductInfo = styled.span`
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 500;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};

  > span {
    font-size: 1.2rem;
    line-height: 1.8rem;
  }

  font-size: 1.2rem;
  line-height: 1.8rem;

  @media screen and (min-width: 768px) {
    line-height: 1.5rem;
    font-size: 1.2rem;

    > span span {
      line-height: 1.5rem;
      font-size: 1.2rem;
    }
  }

  ${ApparelCard}.xs & {
    font-size: 1.3rem;
    line-height: 2.5rem;

    @media screen and (min-width: 768px) {
      font-size: 1.2rem;
      line-height: 1.75rem;
    }
  }

  ${ApparelCard}.lg & {
    font-size: 1.3rem;
    line-height: 2.5rem;

    @media screen and (min-width: 768px) {
      font-size: 1.4rem;
      line-height: 2.7rem;
    }
  }

  ${ApparelCard}.dynamic-size & {
    span {
      font-size: clamp(1.3rem, 2vh, 1.5rem);
    }
  }
`

export const Season = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

export const SeasonIcon = styled.div`
  width: 2rem;
  height: 2rem;
  line-height: 1rem;

  svg {
    circle,
    path,
    g {
      stroke: ${({ theme }) => theme.colors.primary};
      fill: ${({ theme }) => theme.colors.primary};
    }
  }
`

export const SeasonLabel = styled.span`
  font-style: italic;
  font-weight: 400;
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.brand};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};

  > span {
    font-size: 1.2rem;
    line-height: 1rem;
  }

  font-size: 1.2rem;
  line-height: 1rem;

  @media screen and (min-width: 768px) {
    line-height: 1rem;
    font-size: 1.2rem;

    > span span {
      line-height: 1rem;
      font-size: 1.2rem;
    }
  }
`

export const Price = styled.span<{ isDiscounted?: boolean }>`
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 300;
  color: ${({ theme, isDiscounted }) =>
    isDiscounted ? theme.colors.hover : theme.colors.text.primary};
  font-size: 1.6rem;
  line-height: 2.5rem;

  @media screen and (min-width: 768px) {
    font-size: 2rem;
    line-height: 3.2rem;
  }

  ${({ isDiscounted }) =>
    isDiscounted &&
    css`
      text-decoration: line-through;
      text-decoration-thickness: 1px;
    `}

  ${ApparelCard}.xs & {
    font-size: 1.5rem;
    line-height: 2.4rem;

    @media screen and (min-width: 768px) {
      font-size: 1.4rem;
      line-height: 2rem;
    }
  }

  ${ApparelCard}.lg & {
    font-size: 1.5rem;
    line-height: 2.4rem;

    @media screen and (min-width: 768px) {
      font-size: 1.6rem;
      line-height: 2.5rem;
    }
  }

  ${ApparelCard}.dynamic-size & {
    font-size: clamp(1.5rem, 2vh, 1.6rem);
  }
`

export const Separator = styled.div`
  margin: 1rem 0;
  height: 1px;
  width: 75%;
  background: ${({ theme }) => theme.colors.border};

  ${ApparelCard}.xs &, ${ApparelCard}.lg & {
    @media screen and (min-width: 768px) {
      width: 65%;
    }
  }
`

export const Description = styled.div`
  display: inline-block;
  text-align: center;
  padding: 0 1rem;
  margin: 1rem 0;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 2rem;

  @media screen and (min-width: 768px) {
    line-height: 2rem;
  }

  span {
    font-family: ${({ theme }) => theme.fonts.brand};
    font-size: 1.6rem;
    line-height: inherit;

    @media screen and (min-width: 768px) {
      font-size: 1.8rem;
    }
  }

  ${ApparelCard}.xs & {
    margin: 1rem 0 0.2rem;
    padding: 0 1.2rem;
    line-height: 2.2rem;

    @media screen and (min-width: 768px) {
      line-height: 2rem;
    }

    span {
      font-size: 1.5rem;

      @media screen and (min-width: 768px) {
        font-size: 1.5rem;
      }
    }
  }

  ${ApparelCard}.lg & {
    margin: 1rem 0 0.4rem;
    padding: 0 1rem;
    line-height: 2.2rem;

    @media screen and (min-width: 768px) {
      padding: 0 2rem;
      line-height: 2.5rem;
    }

    span {
      font-size: 1.5rem;

      @media screen and (min-width: 768px) {
        font-size: 1.6rem;
      }
    }
  }

  ${ApparelCard}.dynamic-size & {
    span {
      font-size: clamp(1.2rem, 2vh, 1.5rem);
    }
  }
`

export const DescriptionBold = styled.span`
  font-weight: 500;
`

export const DescriptionThin = styled.span`
  font-weight: 300;
`

export const DescriptionBlock = styled(DescriptionThin)`
  display: block;
  width: 100%;
`

export const ApparelVariants = styled.div`
  width: 100%;
  max-width: 25rem;
  margin-bottom: auto;
`

export const SlideUp = styled.div`
  display: flex;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
`
